import React, { useEffect } from 'react';
import classNames from 'classnames';

import { NamedLink } from '../../../components';
import MemberIcon from '../MemberIcon';
import NotificationBell from '../NotificationBell/NotificationBell';
import AvatarMenu from '../AvatarMenu/AvatarMenu';

import css from './MemberTopbar.module.css';

/**
 * The member area's navigation (N1; Member Home design 1b), for signed-in
 * members while REACT_APP_MEMBER_AREA=on.
 *
 *   Desktop: a left sidebar (logo, "New listing", Home · Deals · Inbox · My
 *            listings) and a top row (today's search, Debt, Archive, the bell,
 *            the avatar).
 *   Mobile:  a header (logo, Debt, Archive, search, bell, avatar) and a bottom
 *            bar with five slots, "+ New listing" in the centre.
 *
 * Until phase 2, the links open today's pages (Home → My Hub, Deals → search,
 * My listings → Manage listings). The search stays as it is today (user,
 * 2026-10-21); it is passed in as `searchForm`.
 *
 * The sidebar shows on every page except the inbox (user, 2026-10-22; how the
 * inbox should look is being asked to the client). The inbox's own left
 * panel has a back button to Home, which is enough (user, 2026-10-23), so the
 * top row is the same there.
 */

// Which nav item each page belongs to.
const ACTIVE = {
  home: ['MyHubPage', 'MyHubAllPage'],
  deals: ['SearchPage', 'SearchPageByCategory', 'FavoriteListingsPage'],
  inbox: ['DealInboxPage', 'DealInboxDealPage', 'InboxBasePage', 'InboxPage', 'OrderDetailsPage', 'SaleDetailsPage'],
  mine: ['ManageListingsPage'],
};

const NAV = [
  { key: 'home', label: 'Home', icon: 'home', name: 'MyHubPage' },
  { key: 'deals', label: 'Deals', icon: 'deals', name: 'SearchPage' },
  { key: 'inbox', label: 'Inbox', icon: 'inbox', name: 'InboxBasePage' },
  { key: 'mine', label: 'My listings', icon: 'mine', name: 'ManageListingsPage' },
];

// The account pages (AccountLayout): their own left nav instead of the sidebar.
const ACCOUNT_PAGE_NAMES = [
  'ProfileSettingsPage',
  'DealNotificationsPage',
  'UploadPassportPage',
  'PasswordChangePage',
];

const isActive = (key, currentPage) =>
  !!currentPage && (ACTIVE[key] || []).some(page => String(currentPage).startsWith(page));

// The class that gives full-height pages room for the new bars (see the CSS),
// and the one that drops the sidebar's room on pages without it.
const BODY_CLASS = 'memberAreaLayout';
const NO_SIDEBAR_CLASS = 'memberAreaNoSidebar';

const MemberTopbar = props => {
  const {
    currentUser,
    currentPage,
    inboxCount,
    notificationsNew,
    onNotificationsOpened,
    onLogout,
    onOpenMobileSearch,
    searchForm,
  } = props;

  // While this bar is shown, the page leaves room for the sidebar (desktop)
  // and the bottom bar (mobile), and full-height pages (the inbox) use the
  // new heights.
  useEffect(() => {
    document.body.classList.add(BODY_CLASS);
    return () => document.body.classList.remove(BODY_CLASS);
  }, []);

  // No sidebar on the inbox, nor on the account pages, which have their own
  // left nav with a Back button to Home (user, 2026-10-27).
  const showSidebar =
    !isActive('inbox', currentPage) && !ACCOUNT_PAGE_NAMES.includes(String(currentPage));
  useEffect(() => {
    if (showSidebar) return undefined;
    document.body.classList.add(NO_SIDEBAR_CLASS);
    return () => document.body.classList.remove(NO_SIDEBAR_CLASS);
  }, [showSidebar]);

  const badge = n => (n > 0 ? <span className={css.navBadge}>{n > 99 ? '99+' : n}</span> : null);

  const bell = (
    <NotificationBell
      count={notificationsNew}
      onOpened={onNotificationsOpened}
      panelClassName={css.bellPanel}
    />
  );
  const avatar = <AvatarMenu currentUser={currentUser} onLogout={onLogout} />;
  const extraLinks = (
    <div className={css.extraLinks}>
      <NamedLink name="DebtPage" className={css.extraLink}>
        Debt
      </NamedLink>
      <NamedLink name="BlogPage" className={css.extraLink}>
        Archive
      </NamedLink>
    </div>
  );

  return (
    <div className={css.root}>
      {/* Desktop: the sidebar (not on the inbox). */}
      {showSidebar ? (
        <aside className={css.sidebar}>
          <NamedLink name="MyHubPage" className={css.logo}>
            PARNEXUS
          </NamedLink>
          <NamedLink name="NewListingPage" className={css.newListing}>
            <MemberIcon name="plus" size={16} strokeWidth={2} />
            New listing
          </NamedLink>
          <nav className={css.sideNav} aria-label="Member area">
            {NAV.map(item => (
              <NamedLink
                key={item.key}
                name={item.name}
                className={classNames(css.sideNavItem, {
                  [css.sideNavItemActive]: isActive(item.key, currentPage),
                })}
              >
                <MemberIcon name={item.icon} size={20} />
                <span className={css.sideNavLabel}>{item.label}</span>
                {item.key === 'inbox' ? badge(inboxCount) : null}
              </NamedLink>
            ))}
          </nav>
        </aside>
      ) : null}

      {/* Desktop: the top row. */}
      <div className={css.topRow}>
        <div className={css.search}>{searchForm}</div>
        <span className={css.spacer} />
        {extraLinks}
        {bell}
        {avatar}
      </div>

      {/* Mobile: the header. */}
      <div className={css.mobileHeader}>
        <NamedLink name="MyHubPage" className={css.mobileLogo}>
          PARNEXUS
        </NamedLink>
        {extraLinks}
        <button type="button" className={css.iconButton} onClick={onOpenMobileSearch} aria-label="Search">
          <MemberIcon name="search" size={20} />
        </button>
        {bell}
        {avatar}
      </div>

      {/* Mobile: the bottom bar. */}
      <nav className={css.bottomBar} aria-label="Member area">
        {[NAV[0], NAV[1], { key: 'plus' }, NAV[2], NAV[3]].map(item =>
          item.key === 'plus' ? (
            <NamedLink key="plus" name="NewListingPage" className={css.bottomPlus}>
              <span className={css.bottomPlusCircle}>
                <MemberIcon name="plus" size={22} strokeWidth={2} />
              </span>
              New listing
            </NamedLink>
          ) : (
            <NamedLink
              key={item.key}
              name={item.name}
              className={classNames(css.bottomItem, {
                [css.bottomItemActive]: isActive(item.key, currentPage),
              })}
            >
              <MemberIcon name={item.icon} size={22} />
              {item.label}
              {item.key === 'inbox' && inboxCount > 0 ? (
                <span className={css.bottomBadge}>{inboxCount > 99 ? '99+' : inboxCount}</span>
              ) : null}
            </NamedLink>
          )
        )}
      </nav>
    </div>
  );
};

export default MemberTopbar;
